import React, { useEffect, useRef, useState } from 'react';

import type { FieldProps } from '@atlaskit/form';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, xcss } from '@atlaskit/primitives';

type Props = {
	value: string;
	placeholder?: string;
	appearance?: 'large' | 'compact';
	// Indicates whether the input should be expanded by default (only relevant for 'compact' appearance)
	defaultIsExpanded?: boolean;
	fieldProps?: FieldProps<string, HTMLTextAreaElement>;
	renderLeftControls?: (options: RenderOptions) => React.ReactNode;
	renderRightControls?: (options: RenderOptions) => React.ReactNode;
	onChange: (value: string) => void;
};

type RenderOptions = {
	isExpanded: boolean;
};

export const PromptInput = ({
	value,
	placeholder,
	appearance = 'large',
	defaultIsExpanded = false,
	fieldProps,
	renderLeftControls,
	renderRightControls,
	onChange,
}: Props) => {
	const [isExpanded, setIsExpanded] = useState(defaultIsExpanded);
	const containerRef = useRef<HTMLDivElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	// Once `field-sizing: 'content'` has better browser support, we should use that instead
	const adjustTextareaHeight = () => {
		const textarea = textareaRef.current;
		if (textarea !== null) {
			// Reset height to auto to let browser calculate scrollHeight
			textarea.style.height = 'auto';
			// Set height to scrollHeight to fit content
			textarea.style.height = `${textarea.scrollHeight}px`;
		}
	};

	// Adjust textarea height when user changes the content
	useEffect(() => {
		adjustTextareaHeight();
	}, [value]);

	// Adjust textarea height when expansion state changes, after animation completes
	useEffect(() => {
		const timeoutId = setTimeout(() => {
			adjustTextareaHeight();
		}, ANIMATION_DURATION_MS);
		return () => clearTimeout(timeoutId);
	}, [isExpanded]);

	const onFocus = () => {
		if (appearance === 'compact') {
			setIsExpanded(true);
		}
	};
	// Collapse on blur only if we're in compact mode, the value is empty,
	// and focus is leaving the entire container (not just the textarea).
	const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
		if (appearance !== 'compact' || value.trim() !== '') {
			return;
		}

		const container = containerRef.current;
		const newFocusTarget = event.relatedTarget;

		// Only collapse if focus is leaving the entire container
		if (!container?.contains(newFocusTarget)) {
			setIsExpanded(false);
		}
	};

	return (
		<Box
			ref={containerRef}
			xcss={[
				containerBaseStyles,
				appearance === 'large' && containerLargeStyles,
				appearance === 'compact' && containerCompactStyles,
				appearance === 'compact' && isExpanded && containerExpandedStyles,
			]}
			onBlur={onBlur}
		>
			<Box
				as="textarea"
				ref={textareaRef}
				testId="boysenberry-prompt-input"
				placeholder={placeholder}
				disabled={fieldProps?.isDisabled}
				required={fieldProps?.isRequired}
				aria-labelledby={fieldProps?.['aria-labelledby']}
				value={value}
				rows={1}
				onFocus={onFocus}
				onChange={(e) => onChange(e.target.value)}
				xcss={textAreaStyles}
			/>
			<Inline spread="space-between">
				<Box>{renderLeftControls?.({ isExpanded })}</Box>
				<Box>{renderRightControls?.({ isExpanded })}</Box>
			</Inline>
		</Box>
	);
};

const ANIMATION_DURATION_MS = 200;

const containerBaseStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'space-between',
	minHeight: '24px',
	transition: `min-height ${ANIMATION_DURATION_MS}ms ease-in-out`,
	border: '1px solid',
	borderColor: 'color.border',
	backgroundColor: 'color.background.input',
	borderWidth: 'border.width',
	width: '100%',
	'::placeholder': {
		color: 'color.text.subtlest',
	},
	':hover': {
		backgroundColor: 'color.background.input.hovered',
	},
	':focus-within': {
		backgroundColor: 'color.background.input.hovered',
		overflow: 'auto',
	},
});

const containerCompactStyles = xcss({
	flexDirection: 'row',
	borderRadius: 'border.radius.200',
	padding: 'space.150',
});

const containerLargeStyles = xcss({
	borderRadius: 'border.radius.100',
	minHeight: '180px',
	padding: 'space.100',
});

const containerExpandedStyles = xcss({
	flexDirection: 'column',
	minHeight: '100px',
});

const textAreaStyles = xcss({
	flexGrow: 1,
	padding: 'space.0',
	minHeight: '24px',
	width: '100%',
	resize: 'none',
	overflow: 'hidden',
	font: 'font.body',
	color: 'color.text',
	backgroundColor: 'color.background.neutral.subtle',
	outline: 'none',
	'::placeholder': {
		color: 'color.text.subtlest',
	},
});
